import { z } from 'zod';
import { localDateSchema, syncColumnsSchema } from './common.js';
import {
  applicabilitySchema,
  certaintySchema,
  evidenceKindSchema,
  outcomeHorizonSchema,
  sourceTypeSchema,
  substanceStatusSchema,
} from './evidence.js';

/**
 * IA (Fase 7, docs/AI_ARCHITECTURE.md).
 *
 * Aqui viven **los contratos**, no el cliente: nada de este fichero sabe que
 * el proveedor es Anthropic. El SDK solo existe dentro de
 * `supabase/functions/`, y el resto del sistema habla con estos esquemas.
 *
 * Dos familias distintas:
 *
 *   - Lo que se PERSISTE: peticiones, decisiones y presupuesto. Sirven para
 *     controlar el coste y poder auditar que se acepto y que se rechazo.
 *   - Lo que la IA DEVUELVE: una forma por proposito, validada en el servidor
 *     y otra vez en el dominio antes de tocar la base de datos (§8).
 */

// --------------------------------------------------------------------------
// Vocabulario persistido
// --------------------------------------------------------------------------

/** Para que se llamo a la IA. Cada uno tiene su prompt y su schema de salida. */
export const aiPurposeSchema = z.enum([
  'block_proposal',
  'evidence_review',
  'trend_analysis',
  'explanation',
  'weekly_review',
]);

/**
 * `recipe_generation` estuvo aqui hasta el 21/09/2026 y se fue con la
 * nutricion. **Sale del enum y no se conserva como valor heredado**, al
 * contrario que `fat_loss` en `blockPhaseSchema`, y la diferencia no es de
 * criterio:
 *
 *   - Un `training_blocks.phase` heredado describe un bloque que el usuario
 *     entreno de verdad y que hay que poder leer y pintar.
 *   - Un `ai_requests.purpose` heredado es contabilidad de gasto ya cerrada. Su
 *     columna es `text` sin CHECK, asi que las filas antiguas siguen ahi y se
 *     siguen leyendo; lo unico que el enum decide es que se puede PEDIR, y
 *     pedir una receta ya no se puede.
 *
 * `IACostePage` reparte el gasto por proposito: una fila con un proposito que
 * el enum no conoce se agrupa por su cadena, que es lo correcto para un
 * historico.
 */
export type AIPurpose = z.infer<typeof aiPurposeSchema>;

/**
 * Como acabo la peticion.
 *
 * `schema_error` y `rejected_by_rules` son estados NORMALES, no averias: que el
 * modelo devuelva algo que el dominio rechaza es exactamente lo que la cadena
 * de validacion existe para hacer, y verlo en las estadisticas es informacion
 * sobre la calidad del prompt.
 */
export const aiStatusSchema = z.enum([
  'ok',
  'schema_error',
  'rejected_by_rules',
  'api_error',
  'refusal',
  'budget_exceeded',
  'cache_hit',
]);
export type AIStatus = z.infer<typeof aiStatusSchema>;

// --------------------------------------------------------------------------
// Registro de peticiones
// --------------------------------------------------------------------------

/**
 * Una llamada a la IA.
 *
 * **No se guarda el prompt ni la respuesta cruda** (§14 de DATA_MODEL.md): ni
 * hacen falta para controlar el coste ni conviene tenerlos. Lo que se acepto
 * queda en `ai_decisions`, ya validado.
 */
export const aiRequestSchema = syncColumnsSchema.extend({
  purpose: aiPurposeSchema,
  promptVersion: z.string().min(1).max(40),
  model: z.string().min(1).max(60),
  inputTokens: z.number().int().min(0),
  outputTokens: z.number().int().min(0),
  /** Lectura de cache: cuesta 0,1x la entrada base, asi que se cuenta aparte. */
  cacheReadTokens: z.number().int().min(0).default(0),
  cacheWriteTokens: z.number().int().min(0).default(0),
  costCents: z.number().min(0),
  status: aiStatusSchema,
  latencyMs: z.number().int().min(0),
  /** SHA-256 del contexto canonico, para la cache de aplicacion (§7.2). */
  contextHash: z.string().length(64),
});

export type AIRequest = z.infer<typeof aiRequestSchema>;

/**
 * Lo que la IA propuso y si se acepto.
 *
 * `accepted = false` con su motivo es tan util como `true`: es la prueba de que
 * el dominio manda sobre el modelo y no al reves.
 */
export const aiDecisionSchema = syncColumnsSchema.extend({
  aiRequestId: z.uuid(),
  decisionId: z.uuid().nullable(),
  payload: z.record(z.string(), z.unknown()),
  /**
   * `null` = propuesta registrada y todavia sin juzgar.
   *
   * La Edge Function inserta la fila en cuanto la salida valida contra el
   * schema; el veredicto lo pone el dominio despues, ya en el cliente, tras
   * aplicar sus reglas y los limites de seguridad. Sin el nulo habria que
   * elegir entre marcar como rechazado lo que nadie ha mirado o como aceptado
   * lo que nadie ha validado, y las dos cosas mienten.
   */
  accepted: z.boolean().nullable(),
  rejectionReason: z.string().max(300).nullable(),
});

export type AIDecision = z.infer<typeof aiDecisionSchema>;

/** Presupuesto mensual. Un tope de seguridad, no una prevision. */
export const aiUsageBudgetSchema = syncColumnsSchema.extend({
  /** Primer dia del mes. */
  periodMonth: z.string().regex(/^\d{4}-\d{2}-01$/),
  spentCents: z.number().min(0).default(0),
  budgetCents: z.number().int().min(0).max(100000).default(500),
  requestsCount: z.number().int().min(0).default(0),
});

export type AIUsageBudget = z.infer<typeof aiUsageBudgetSchema>;

/** Presupuesto por defecto: 5 €/mes (§7.3). */
export const DEFAULT_BUDGET_CENTS = 500;

/** Proporcion del presupuesto a partir de la cual se avisa (§7.3). */
export const BUDGET_WARNING_RATIO = 0.8;

// --------------------------------------------------------------------------
// Salidas de la IA, por proposito
// --------------------------------------------------------------------------

/**
 * Lo que un REDACTOR señala para revisión (encargo del 23/09/2026, §3 y §4).
 *
 * Hasta el 23/09/2026 el papel del redactor le ordenaba explicar cualquier
 * decisión «aunque sea discutible, sin discutirla ni matizarla». Con eso, una
 * decisión contradictoria con sus propios datos, o tomada junto a una señal
 * preocupante, salía justificada con buena prosa: la IA ponía su redacción al
 * servicio de lo que hubiera que defender.
 *
 * Ahora el redactor puede decirlo aquí. Lo que NO puede es cambiar la
 * decisión: no hay campo para otra carga, otras series ni otra fase, y la
 * pantalla, cuando esto no es null, enseña el texto DETERMINISTA —no una
 * justificación redactada— con el aviso al lado.
 *
 * `null` significa «no veo nada que señalar», no «la decisión es segura»: la
 * seguridad la deciden las reglas del dominio, no la ausencia de un aviso.
 * Es obligatorio y admite null, no opcional: la salida estructurada tiene que
 * declararlo siempre, y así una respuesta que lo omite se distingue de una que
 * no señala nada.
 */
export const aiReviewConcernSchema = z.string().min(10).max(300).nullable();

/**
 * Explicacion redactada («¿Por que?», §2).
 *
 * Es la salida mas acotada a proposito, y la primera que se conecta: la IA
 * **no decide nada** aqui, solo pone en castellano una decision que ya se
 * tomo de forma determinista. Si falla, la plantilla determinista dice lo
 * mismo peor escrito, y no se pierde ninguna informacion.
 */
export const aiExplanationSchema = z.object({
  /** Una frase. Lo que se ensena cuando el panel esta plegado. */
  headline: z.string().min(10).max(160),
  /** El desarrollo. Sin HTML ni enlaces: se sanea antes de renderizar (§8). */
  body: z.string().min(20).max(900),
  concern: aiReviewConcernSchema,
});

export type AIExplanation = z.infer<typeof aiExplanationSchema>;

/**
 * Interpretacion del check-in semanal.
 *
 * La DECISION sigue siendo determinista: la IA redacta el porque, no elige
 * entre mantener y ajustar. Por eso este esquema no tiene ningun campo de
 * decision.
 */
export const aiWeeklyReviewSchema = z.object({
  summary: z.string().min(20).max(700),
  interpretation: z.string().min(20).max(900),
  concern: aiReviewConcernSchema,
});

export type AIWeeklyReview = z.infer<typeof aiWeeklyReviewSchema>;

/** Analisis de una tendencia con senales en conflicto. */
export const aiTrendAnalysisSchema = z.object({
  interpretation: z.string().min(20).max(900),
  /** Que senal considera dominante, de las que se le pasaron. */
  dominantSignal: z.string().min(2).max(40),
  /** 0 a 1. Se ensena como texto, nunca como porcentaje de acierto. */
  confidence: z.number().min(0).max(1),
  concern: aiReviewConcernSchema,
});

export type AITrendAnalysis = z.infer<typeof aiTrendAnalysisSchema>;

/*
 * `exercise_selection` estuvo aqui hasta el 22/09/2026, y se retiro al ampliar
 * `block_proposal`.
 *
 * Su trabajo era elegir que ejercicio iba en cada hueco de una plantilla ya
 * escrita. Desde que la IA propone las SESIONES —cuantas, que entrena cada una
 * y con que ejercicios—, elegir los ejercicios es parte de diseñar el bloque y
 * no una pregunta aparte. Mantener los dos significaba dos prompts, dos
 * esquemas y dos validadores para la misma decision, y una llamada de mas cada
 * vez que se creara un bloque.
 *
 * Sale del enum por el mismo motivo que `recipe_generation`: un
 * `ai_requests.purpose` antiguo es contabilidad cerrada en una columna `text`
 * sin CHECK, asi que las filas viejas se siguen leyendo y agrupando.
 */

/**
 * Estructura del bloque que viene (§2).
 *
 * Lo que se le pregunta es **lo que de verdad cambia algo**: cuantas semanas
 * dura y cuantas series semanales lleva cada musculo. No se le pregunta el
 * split, y no por prudencia: hoy solo dos splits tienen plantillas y las dos
 * apuntan a las mismas sesiones (`splits.ts`), asi que elegirlo no cambiaria
 * nada. Ofrecer una eleccion sin efecto y enseñarla como si lo tuviera es peor
 * que no ofrecerla.
 *
 * `volumeTargets` es un ARRAY y no un objeto indexado por musculo. Dos motivos:
 * el JSON Schema de un `partialRecord` con veinte claves opcionales es mucho
 * peor de leer para el modelo que una lista de pares, y con la lista el nombre
 * del musculo llega como DATO, que es lo que permite rechazar uno inventado en
 * vez de que se pierda en una clave que nadie mira.
 */
/**
 * Un hueco de la sesion, tal y como lo propone el modelo.
 *
 * `exerciseSlug` es texto libre A PROPOSITO, igual que en
 * `aiExerciseSelectionSchema`: un ejercicio inventado es justo lo que el
 * dominio tiene que poder rechazar, y un esquema que lo impidiera dejaria esa
 * comprobacion sin nada que comprobar y sin forma de saber cuantas veces pasa.
 *
 * El ROL no es decorativo. Ordena la sesion (`ordenarSesion`: los basicos
 * primero, siempre, porque un aislamiento delante cambia el estimulo del
 * basico en vez de aumentar el del musculo priorizado) y decide cuantas series
 * y con que RIR se prescribe, via `WeekParameters`.
 */
export const aiSessionExerciseSchema = z.object({
  exerciseSlug: z.string().min(1).max(64),
  role: z.enum(['main', 'secondary', 'accessory']),
});

/** Una sesion del bloque: que entrena y con que. */
export const aiSessionPlanSchema = z.object({
  /**
   * Como se llama la sesion. NO es decorativo: `exercisesOfBlock` recupera por
   * NOMBRE los ejercicios ya elegidos cuando se rematerializa una semana, asi
   * que dos sesiones con el mismo nombre se pisarian los ejercicios.
   */
  name: z.string().min(2).max(40),
  objective: z.string().min(5).max(140),
  exercises: z.array(aiSessionExerciseSchema).min(1).max(12),
});

export type AISessionPlan = z.infer<typeof aiSessionPlanSchema>;

/**
 * Estructura del bloque que viene (§7 y §9 del encargo).
 *
 * QUE CAMBIO EL 22/09/2026, Y POR QUE ERA NECESARIO
 *
 * Hasta hoy esto tenia tres campos: `weeks`, `volumeTargets` y `rationale`. Es
 * decir, la IA movia dos numeros dentro de un bloque que ya estaba disenado:
 * las cuatro sesiones, sus 27 huecos, el orden y los roles salian de una
 * plantilla escrita a mano (`splits.ts`), y encima el volumen se recortaba a
 * ±20 % sobre lo que esa misma plantilla producia. Llamar a eso «la IA disena
 * la programacion» era falso, y el §7 del encargo lo senala: «evita conservar
 * restricciones de las plantillas que hagan imposible esa participacion y, al
 * mismo tiempo, afirmar que la IA disena la programacion».
 *
 * Ahora propone las SESIONES: cuantas, como se llaman, que entrena cada una,
 * con que ejercicios y en que rol. Eso es lo que de verdad decide la
 * frecuencia, el split y el reparto, porque las tres cosas son consecuencia de
 * como se agrupan las sesiones y no una etiqueta aparte. Por eso no hay un
 * campo `split`: pedirlo ademas seria pedir dos veces lo mismo y abrir la
 * puerta a que se contradigan.
 *
 * LO QUE SIGUE SIENDO DETERMINISTA, y no por prudencia sino porque es otra
 * cosa: la RAMPA entre semanas —cuantas series y con que RIR se progresa de la
 * semana 1 a la 6, donde cae el deload— la pone `PHASE_RAMPS`. El §7 reparte
 * asi: la IA propone la programacion, el motor local ejecuta las reglas de
 * progresion. Una rampa propuesta por el modelo tendria que validarse contra
 * el limite de ±20 % semanal, la insercion de deloads y las bandas, y acabaria
 * recortada hasta parecerse a la que ya hay.
 *
 * NOTA SOBRE LOS LIMITES: `output_config.format.schema` no admite `min`/`max`
 * de array ni `maxLength`, asi que los topes de aqui NO viajan a la API. Van
 * escritos con palabras en el prompt y se imponen AQUI al validar. Es la misma
 * trampa que costo cuatro propositos el 01/09/2026 (ver `esquema.ts`).
 */
export const aiBlockProposalSchema = z.object({
  weeks: z.number().int().min(1).max(16),
  /**
   * Las sesiones de una semana tipo, en el orden en que se entrenan.
   *
   * El tope de 6 no es arbitrario: `SAFETY_LIMITS.session.maxStrengthSessionsPerWeek`
   * es 5, y se deja uno de margen para que el dominio pueda RECHAZAR una
   * propuesta de 6 en vez de que la API la corte antes y nunca sepamos que el
   * modelo se pasa.
   */
  sessions: z.array(aiSessionPlanSchema).min(1).max(6),
  volumeTargets: z
    .array(
      z.object({
        muscle: z.string().min(2).max(30),
        /** Series efectivas por semana. */
        sets: z.number().int().min(0).max(40),
      }),
    )
    .max(24),
  /** Por que este bloque y no otro. Texto libre: pasa por `validateAIProse`. */
  rationale: z.string().min(20).max(600),
  /**
   * Las fichas de evidencia en las que se apoya, por su referencia corta
   * («E1», «E2»…) y la afirmación que respaldan (§14).
   *
   * NO es un identificador de fila ni un enlace: el contexto numera las fichas
   * activas y el cliente traduce la referencia a la ficha. Una referencia que
   * no esté en el contexto no se resuelve y se cae, así que una cita inventada
   * es imposible, no solo improbable (`resolveCitations`). Vacío es una
   * respuesta válida, y la correcta cuando no hay fichas.
   */
  citations: z
    .array(
      z.object({
        ref: z.string().min(2).max(6),
        claim: z.string().min(2).max(60),
      }),
    )
    .max(6),
});

export type AIBlockProposal = z.infer<typeof aiBlockProposalSchema>;


/**
 * Una ficha tal y como la DEVUELVE el modelo (§8 del encargo).
 *
 * Es el contenido, no la fila: sin `id`, sin columnas de sincronizacion, sin
 * `status` y sin `version`. Esos los pone el cliente, y esa frontera es
 * deliberada —el §8 separa «busqueda y sintesis» de «evaluacion y activacion»—:
 * toda ficha que entra nace `candidate`, y que el modelo no pueda decir otra
 * cosa es lo que impide que se active a si misma.
 *
 * `url` NO es texto libre que el modelo componga. La Edge Function le pasa lo
 * que las herramientas de servidor recuperaron de verdad, y `validateRetrieved`
 * rechaza cualquier ficha cuya URL no este entre las recuperadas. Es la unica
 * forma de que una referencia inventada sea IMPOSIBLE y no solo improbable.
 */
export const aiEvidenceCardSchema = z.object({
  title: z.string().min(5).max(300),
  authors: z.string().min(2).max(300),
  publication: z.string().min(2).max(200),
  year: z.number().int().min(1950).max(2100),
  doi: z.string().max(120).nullable(),
  pmid: z.string().max(20).nullable(),
  url: z.string().max(500).nullable(),
  sourceType: sourceTypeSchema,
  kind: evidenceKindSchema,
  peerReviewed: z.boolean(),
  population: z.string().min(5).max(400),
  substanceStatus: substanceStatusSchema,
  intervention: z.string().min(5).max(400),
  comparison: z.string().max(400).nullable(),
  outcomeHorizon: outcomeHorizonSchema,
  durationWeeks: z.number().int().min(0).max(520).nullable(),
  findings: z.string().min(10).max(1200),
  limitations: z.string().min(10).max(1200),
  certainty: certaintySchema,
  applicability: applicabilitySchema,
  claims: z.array(z.string().min(2).max(60)).min(1).max(12),
  fullTextAccess: z.boolean(),
  literatureThrough: localDateSchema.nullable(),
});

export type AIEvidenceCard = z.infer<typeof aiEvidenceCardSchema>;

/**
 * Lo que devuelve una revision de evidencia.
 *
 * Trae las fichas Y el registro de la busqueda, en la misma respuesta, porque
 * el §8 pide guardar las dos cosas y separarlas en dos llamadas dejaria el
 * registro a merced de que la segunda funcionara.
 *
 * `retrievedCount` y `keptCount` son del modelo y **no se creen**: el cliente
 * los compara con lo que de verdad llega. Se piden igual porque la diferencia
 * entre lo que dice haber mirado y lo que entrega es informacion sobre la
 * busqueda, no ruido.
 */
export const aiEvidenceReviewSchema = z.object({
  queries: z.array(z.string().min(2).max(300)).min(1).max(20),
  coversFrom: localDateSchema.nullable(),
  coversTo: localDateSchema.nullable(),
  selectionCriteria: z.string().min(10).max(800),
  retrievedCount: z.number().int().min(0).max(500),
  keptCount: z.number().int().min(0).max(500),
  /**
   * Lo que la busqueda NO pudo hacer. Con minimo de longitud a proposito:
   * «ninguna» es valido pero hay que escribirlo, porque un hueco en blanco se
   * lee como «no las hay» cuando casi siempre significa «no se miro».
   */
  limitations: z.string().min(5).max(800),
  /**
   * Puede venir VACIO, y no es un fallo: «he buscado y no hay nada aplicable»
   * es un resultado, y uno importante. Por eso `.min(0)` explicito.
   */
  sources: z.array(aiEvidenceCardSchema).min(0).max(12),
});

export type AIEvidenceReview = z.infer<typeof aiEvidenceReviewSchema>;

/**
 * Lo que el CLIENTE recibe: la respuesta del modelo mas la verdad del servidor.
 *
 * `retrievedUrls` NO lo escribe el modelo. Lo añade la Edge Function con lo que
 * las herramientas de servidor trajeron de verdad, y por eso el esquema que
 * viaja a la API es `aiEvidenceReviewSchema` —sin este campo— mientras que el
 * que valida el cliente es este.
 *
 * Esa asimetria es el mecanismo entero del §14. Con la lista de lo recuperado
 * como DATO al lado de las fichas, `validateRetrieved` puede rechazar una ficha
 * cuya URL no este en ella. El modelo no puede falsear esa lista porque no la
 * escribe, y no puede colar una fuente que no se descargo porque su URL no
 * estaria. Una referencia inventada deja de ser improbable y pasa a ser
 * imposible.
 */
export const aiEvidenceReviewPayloadSchema = aiEvidenceReviewSchema.extend({
  retrievedUrls: z.array(z.string().max(500)).max(200),
});

export type AIEvidenceReviewPayload = z.infer<typeof aiEvidenceReviewPayloadSchema>;

// --------------------------------------------------------------------------
// El cable entre el cliente y la Edge Function `coach-ai`
// --------------------------------------------------------------------------

/**
 * Peticion a la Edge Function.
 *
 * `contextHash` lo calcula el CLIENTE y el servidor lo recalcula: si no
 * coinciden se rechaza. No es desconfianza, es que el hash es la clave de la
 * cache (§7.2) y un hash que no describe su contexto devolveria la respuesta
 * de otra pregunta.
 */
export const coachAIRequestSchema = z.object({
  purpose: aiPurposeSchema,
  /** Ya recortado por el dominio. Pasa por `auditAIContext` en los dos lados. */
  context: z.record(z.string(), z.unknown()),
  contextHash: z.string().length(64),
});

export type CoachAIRequest = z.infer<typeof coachAIRequestSchema>;

/**
 * Por que no hubo respuesta utilizable.
 *
 * Vive en el contrato, no en el dominio, porque viaja por el cable: la Edge
 * Function lo produce y el dominio lo consume, y con dos definiciones una de
 * las dos se queda vieja sin que nada falle.
 */
export const aiErrorSchema = z.discriminatedUnion('kind', [
  /** Red, 5xx, timeout, o el modelo declino responder (`stop_reason: refusal`). */
  z.object({ kind: z.literal('unavailable'), detail: z.string().max(300).optional() }),
  z.object({
    kind: z.literal('budget_exceeded'),
    spentCents: z.number(),
    budgetCents: z.number(),
  }),
  /** Estructuralmente valido pero fuera de contrato. */
  z.object({ kind: z.literal('invalid_output'), details: z.string().max(500) }),
  /** Valido, pero el dominio lo rechaza por sus propias reglas. */
  z.object({ kind: z.literal('rejected_by_rules'), violations: z.array(z.string()) }),
  /** El usuario ha apagado la IA, o el contexto no paso la auditoria. No es una averia. */
  z.object({ kind: z.literal('disabled'), detail: z.string().max(300).optional() }),
  /**
   * Demasiadas peticiones en poco tiempo (SEC-001).
   *
   * Tampoco es una averia, y por eso no es `unavailable`: el sistema esta
   * funcionando y lo que dice es «ahora no, vuelve en un momento». El
   * presupuesto acota CUANTO se gasta; esto, cuan deprisa. La regla vive en
   * `supabase/functions/coach-ai/limite.ts`.
   */
  z.object({
    kind: z.literal('rate_limited'),
    retryAfterSeconds: z.number().int().min(1).max(60),
  }),
]);

export type AIError = z.infer<typeof aiErrorSchema>;

/** Lo que la Edge Function devuelve. Nunca lanza hacia el cliente. */
export const coachAIResponseSchema = z.discriminatedUnion('ok', [
  z.object({
    ok: z.literal(true),
    payload: z.record(z.string(), z.unknown()),
    aiRequestId: z.uuid(),
    aiDecisionId: z.uuid(),
    /** `cache_hit` cuando no se llamo a la API: coste cero. */
    status: aiStatusSchema,
    costCents: z.number().min(0),
    spentCents: z.number().min(0),
    budgetCents: z.number().int().min(0),
    /**
     * La versión del prompt con la que se generó (o se sirvió de caché) esta
     * respuesta. Hasta el 24/09/2026 el cliente la suponía y la revisión de
     * literatura guardaba `evidence_review.v1` con el prompt en la v5.
     * Opcional: una función desplegada antes no la manda y su respuesta tiene
     * que seguir valiendo.
     */
    promptVersion: z.string().min(1).max(40).optional(),
  }),
  z.object({ ok: z.literal(false), error: aiErrorSchema }),
]);

export type CoachAIResponse = z.infer<typeof coachAIResponseSchema>;
